import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  CircularProgress,
  IconButton,
  useTheme,
  Tooltip,
  Divider,
  Fade,
  Slide,
  Grow,
  Alert,
  Fab,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import LayoutWrapper from '../layout/UILayout';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  KeyboardArrowLeftOutlined,
  CheckCircle,
  AutoAwesome,
  Cancel,
  Undo,
} from '@mui/icons-material';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import FileUpload from './FileUpload';
import TranscriptPicker from './TranscriptPicker';
import TemplateLoading from './TemplateLoading';
import TemplatePreview from './TemplatePreview';
import ChatInterface from './ChatInterface';
import { TemplateBuilderProvider, useTemplateBuilder } from './TemplateBuilderContext';
import { styled, keyframes } from '@mui/material/styles';
import TemplateSectionsList from './TemplateSectionsList';
import { TutorialProvider, useTutorial } from '../Tutorial/TutorialContext';
import { TutorialStep } from '../Tutorial/TutorialStep';
import APIService from '../../services/APIService';
import { useEventTracking } from '@services/statsig/hooks';

// Animation keyframes
const scaleUp = keyframes`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 200, 83, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(0, 200, 83, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(0, 200, 83, 0);
  }
`;

// Styled components
const SuccessIcon = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  backgroundColor: theme.palette.success.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.common.white,
  animation: `${pulse} 1.5s ease-in-out`,
}));

// Define the TemplateViewTutorialWrapper outside the main component
const TemplateViewTutorialWrapper: React.FC<{
  slideDirection: 'left' | 'right';
  showContent: boolean;
}> = ({ slideDirection, showContent }) => {
  const { isActive } = useTutorial(); // Call hook at the top level of this wrapper

  return (
    <Slide
      direction={slideDirection === 'left' ? 'left' : 'right'}
      in={showContent}
      mountOnEnter
      unmountOnExit
      timeout={500}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          height: '100%',
        }}
      >
        {/* 2/3 width - Template Preview */}
        <Box sx={{ width: { xs: '100%', md: '66.66%' }, height: '100%' }}>
          <TutorialStep
            step={0}
            description="Here's a preview of what your template will look like, using the information from the transcript you selected before. You can edit section names, descriptions, and reorder them."
            placement="right"
            fullHeight
          >
            <Grow in={showContent} timeout={800}>
              <Box sx={{ height: '100%' }}>
                <TemplatePreview />
              </Box>
            </Grow>
          </TutorialStep>
        </Box>

        {/* 1/3 width - Chat Interface takes full height */}
        <Box
          sx={{
            width: { xs: '100%', md: '33.33%' },
            height: '100%',
          }}
        >
          <TutorialStep
            step={1}
            description="Use the chat interface to refine your template. Ask for new sections, modify existing ones, or change the overall structure and formatting."
            placement="left"
            fullHeight
          >
            <Grow in={showContent} timeout={1000}>
              <Box sx={{ height: '100%' }}>
                <ChatInterface />
              </Box>
            </Grow>
          </TutorialStep>
        </Box>
        {isActive && (
          <TutorialStep
            step={2}
            description="When you're done, click the 'Save Template' button in the top right to save your template. You can then use it in your encounter notes."
            placement="right-start"
            fullHeight
          >
            <Box></Box>
          </TutorialStep>
        )}
      </Box>
    </Slide>
  );
};

const TemplateBuilderContent: React.FC = () => {
  const subjectLanguage = useSubjectLanguage();
  const { getAccessToken } = useAccessToken();
  const navigate = useNavigate();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { template: templateMetrics } = useEventTracking();
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const {
    viewState,
    previousViewState,
    showContent,
    showAnalysisSuccessAnimation,

    templateSections,
    sessionId,

    tasksState,
    currentPollingTaskId,
    processingSectionIds,

    handleBackClick,
    handleSaveTemplate,
  } = useTemplateBuilder();

  // Derived state from tasksState
  const isFileProcessing = tasksState.fileProcessing === 'processing';
  const isProcessingTranscript = tasksState.transcriptAnalysis === 'processing';
  
  // Track AI builder session start
  useEffect(() => {
    if (!hasTrackedStart && sessionId && viewState === 'template') {
      templateMetrics.aiChatStarted(sessionId, {
        upload_type: previousViewState === 'upload' ? 'file' : 'transcript',
      });
      templateMetrics.customizationStarted(sessionId, {
        source: 'ai_builder',
        initial_sections_count: templateSections?.length || 0,
      });
      setHasTrackedStart(true);
    }
  }, [sessionId, viewState, hasTrackedStart, templateMetrics, templateSections, previousViewState]);

  // Get direction for slide transitions
  const getSlideDirection = (
    prev: 'upload' | 'transcript' | 'template',
    next: 'upload' | 'transcript' | 'template',
  ) => {
    const states = ['upload', 'transcript', 'template'];
    const prevIndex = states.indexOf(prev);
    const nextIndex = states.indexOf(next);

    return prevIndex < nextIndex ? 'left' : 'right';
  };

  // Get a human-readable stage name

  const handleCancelTemplate = () => {
    setOpenCancelDialog(true);
  };

  const handleConfirmCancel = async () => {
    // Remove localStorage key
    localStorage.removeItem('template_builder_session_id');

    // Stub API call to discard the template
    if (sessionId) {
      console.log('Discarding template session:', sessionId);
      const accessToken = await getAccessToken();
      await APIService.makeAPIPostRequest({
        requestString: '/templatebuilder/rejectTemplate',
        body: { session_id: sessionId },
        accessToken: accessToken,
      });
    }

    // Close dialog
    setOpenCancelDialog(false);

    // Navigate back to templates page
    navigate('/templates');
  };

  // Wrapper function to handle saving state
  const handleSaveClick = async () => {
    setIsSaving(true);
    try {
      // Track save attempt
      templateMetrics.saveAttempted(sessionId || 'unknown', {
        sections_count: templateSections?.length || 0,
        created_via: 'ai_builder',
        success: true,
      });
      
      await handleSaveTemplate();
      // Optionally add success feedback here if needed
    } catch (error) {
      console.error('Error saving template:', error);
      // Track save failure
      templateMetrics.saveAttempted(sessionId || 'unknown', {
        sections_count: templateSections?.length || 0,
        created_via: 'ai_builder',
        success: false,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Render different content based on the current view state
  const renderMainContent = () => {
    const slideDirection = getSlideDirection(previousViewState, viewState);

    // If showing analysis success animation, render that instead of normal content
    if (showAnalysisSuccessAnimation) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            animation: `${scaleUp} 0.5s ease-out forwards`,
          }}
        >
          <SuccessIcon>
            <AutoAwesome fontSize="large" />
          </SuccessIcon>
          <Typography variant="h6" sx={{ mt: 2, color: 'success.main', fontWeight: 600 }}>
            Template Created Successfully!
          </Typography>
        </Box>
      );
    }

    switch (viewState) {
      case 'upload':
        return (
          <Fade in={showContent} timeout={500}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
                height: '100%',
                width: '100%',
              }}
            >
              <FileUpload />
            </Box>
          </Fade>
        );

      case 'transcript':
        return (
          <TutorialProvider
            tutorialKey="template-builder-transcript"
            tutorialVersion={1}
            autoStart={true}
            totalSteps={2}
          >
            <Slide
              direction={slideDirection === 'left' ? 'left' : 'right'}
              in={showContent}
              mountOnEnter
              unmountOnExit
              timeout={500}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2,
                  height: '100%',
                }}
              >
                {/* 2/3 width - Transcript Picker */}
                <Box sx={{ width: { xs: '100%', md: '66.66%' }, height: '100%' }}>
                  <TutorialStep
                    step={0}
                    placement="right"
                    description="Select a transcript from the list below. This example transcript will be used to show you what your template will look like after an actual encounter."
                  >
                    <Grow in={showContent} timeout={800}>
                      <Box sx={{ height: '100%' }}>
                        <TranscriptPicker />
                      </Box>
                    </Grow>
                  </TutorialStep>
                </Box>

                {/* 1/3 width - Template Loading/Sections List */}
                <Box sx={{ width: { xs: '100%', md: '33.33%' }, height: '100%' }}>
                  <TutorialStep
                    step={1}
                    placement="left"
                    description="Once your uploaded file is processed, the generated template sections will appear here."
                    fullHeight
                  >
                    <Box sx={{ width: '100%' }}>
                      <Grow in={showContent} timeout={1000}>
                        <Box sx={{ position: 'relative', height: '100%' }}>
                          <Fade
                            in={templateSections.length === 0}
                            timeout={{ enter: 800, exit: 500 }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                opacity: templateSections.length === 0 ? 1 : 0,
                                zIndex: templateSections.length === 0 ? 2 : 1,
                              }}
                            >
                              <TemplateLoading />
                            </Box>
                          </Fade>
                          <Fade
                            in={templateSections.length > 0}
                            timeout={{ enter: 800, exit: 300 }}
                            style={{
                              transitionDelay: templateSections.length > 0 ? '300ms' : '0ms',
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                width: '100%',
                                height: '100%',
                                opacity: templateSections.length > 0 ? 1 : 0,
                                zIndex: templateSections.length > 0 ? 2 : 1,
                              }}
                            >
                              <TemplateSectionsList />
                            </Box>
                          </Fade>
                        </Box>
                      </Grow>
                    </Box>
                  </TutorialStep>
                </Box>
              </Box>
            </Slide>
          </TutorialProvider>
        );

      case 'template':
        return (
          <TutorialProvider
            tutorialKey="template-builder-template"
            tutorialVersion={1}
            totalSteps={3}
            autoStart={true}
          >
            {/* Pass props to the externally defined component */}
            <TemplateViewTutorialWrapper
              slideDirection={slideDirection}
              showContent={showContent}
            />
          </TutorialProvider>
        );

      default:
        return <Box>Invalid view state</Box>;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      {/* Header with back button and title */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handleBackClick} sx={{ mr: 1 }} aria-label="back">
            <KeyboardArrowLeftOutlined />
          </IconButton>
          <Typography variant="h5" component="h1">
            Build Template
          </Typography>
        </Box>

        {(viewState === 'template' || viewState === 'transcript') && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={handleCancelTemplate}
            >
              Cancel
            </Button>
            {viewState === 'template' && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveClick}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Saving...
                  </>
                ) : (
                  'Save Template'
                )}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {/* File processing indicator */}
      {isFileProcessing && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'info.light',
            color: 'info.contrastText',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={20} thickness={5} sx={{ mr: 2, color: 'inherit' }} />
          <Typography variant="body2">
            Processing your file in the background. This may take a few minutes.
          </Typography>
        </Paper>
      )}

      {/* Transcript analysis indicator */}
      {isProcessingTranscript && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: 'info.light',
            color: 'info.contrastText',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={20} thickness={5} sx={{ mr: 2, color: 'inherit' }} />
          <Typography variant="body2">Analyzing transcript and generating template...</Typography>
        </Paper>
      )}

      {/* Main content that changes based on the current view state */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {renderMainContent()}
      </Box>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle>Discard Template?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to discard this template? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>No, Keep Editing</Button>
          <Button onClick={handleConfirmCancel} color="error" autoFocus>
            Yes, Discard
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const TemplateBuilderView: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <LayoutWrapper>
      <LayoutWrapper.MainContent>
        <TemplateBuilderProvider>
          <TemplateBuilderContent />
        </TemplateBuilderProvider>
      </LayoutWrapper.MainContent>
    </LayoutWrapper>
  );
};

export default TemplateBuilderView;

// Add keyframe animation for pulsing effect
const styleElement = document.createElement('style');
styleElement.textContent = `
  @keyframes pulse {
    0% {
      transform: scale(0.95);
      opacity: 0.7;
    }
    50% {
      transform: scale(1.05);
      opacity: 1;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.7;
    }
  }
`;
document.head.appendChild(styleElement);
